<template>
  <PageTransition>
    <div>
      <v-overlay
        scrim="rgb(var(--v-theme-background))"
        v-if="!ready"
        :modelValue="isLoading"
        class="align-center justify-center"
        persistent
      >
        <transition name="fade" mode="out-in">
          <v-progress-circular
            v-if="!errorMessage"
            data-spec="loader"
            indeterminate
            size="64"
            color="rgb(var(--v-theme-color))"
          />
          <div v-else class="error-wrapper">
            <div>
              <v-icon
                class="error__icon"
                color="rgb(var(--v-theme-color))"
                size="128"
              >
                mdi-alert-circle-outline
              </v-icon>
            </div>
            <div>{{ $t('errors.appLoading') }}</div>
            <div class="error__message">{{ errorMessage }}</div>
          </div>
        </transition>
      </v-overlay>
      <slot v-else :ready="ready" />
    </div>
  </PageTransition>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useAuth0 } from '@auth0/auth0-vue';
import PageTransition from '@/components/pageTransition.vue';
import { composeMessage } from '@/services/errorHandler';
import { useDirectStore } from '@/composables/store';
import { initializeAuthSingleton } from '@/auth'; // Import the auth initializer

// Initialize auth singleton for use elsewhere in the app
initializeAuthSingleton();

const store = useDirectStore();
const route = useRoute();
const errorMessage = ref('');
const sourcesLoaded = ref(false);

// Use Auth0 reactive states
const {
  isAuthenticated,
  isLoading: auth0Loading,
  getAccessTokenSilently,
} = useAuth0();

// Only load account when authenticated and not already loaded
const accountLoaded = computed(() => store.account.getLoaded());

// Check if auth is required for current route
const requiresAuth = computed(() =>
  route.matched.some((r) => r.meta.requiresAuth)
);

// Determine if we're ready to show the content
const ready = computed(
  () =>
    // If auth is required, we need authentication and account data
    (!requiresAuth.value || (isAuthenticated.value && accountLoaded.value)) &&
    // Always need sources to be loaded
    sourcesLoaded.value &&
    // Not still loading auth
    !auth0Loading.value
);

// Loading state for the overlay
const isLoading = computed(
  () =>
    !ready.value ||
    auth0Loading.value ||
    (requiresAuth.value && !accountLoaded.value)
);

// When authentication state changes and we need auth, load account
watch(
  [isAuthenticated, requiresAuth],
  async ([authenticated, needsAuth]) => {
    if (authenticated && needsAuth && !accountLoaded.value) {
      try {
        await store.account.dispatchLoadAccount();
      } catch (err) {
        errorMessage.value = composeMessage(err);
        try {
          // Try to get a fresh token with the correct cache option
          await getAccessTokenSilently({
            cacheMode: 'off', // Use "off" instead of "no-cache"
          });
          window.location.reload();
        } catch {
          // if that also fails, leave the errorMessage on-screen
        }
      }
    }
  },
  { immediate: true }
);

// When account is loaded, load progress model
watch(
  accountLoaded,
  (loaded) => {
    if (loaded) {
      store.progressModel.dispatchLoadFullResource({});
    }
  },
  { immediate: true }
);

onMounted(() => {
  window.addEventListener('load', () => (sourcesLoaded.value = true));

  // If the page is already loaded
  if (document.readyState === 'complete') {
    sourcesLoaded.value = true;
  }
});
</script>
<style lang="scss" scoped>
.error-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: rgb(var(--v-theme-color));
  line-height: 1.4;
}
.error__icon {
  opacity: 0.15;
  margin-bottom: $space-regular;
}
.error__message {
  margin-top: $space-x-small;
  opacity: 0.5;
  font-size: 85%;
}
.error__reload {
  padding: 10px;
  font-weight: normal;
  font-size: 85%;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
